import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import DesktopLayout from "@/components/Layout/DesktopLayout";
import { api, formatApiError } from "@/api/client";
import { Loader2, Plus, Trash2, X, Pencil } from "lucide-react";
import { toast } from "sonner";

const FIELD = "w-full bg-white border border-slate-300 rounded-sm p-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none";
const LABEL = "hk-tiny-label block mb-1";

export default function QuoteBuilder() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const editing = !!id;
  const [catalog, setCatalog] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [lines, setLines] = useState([]);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(emptyDraft());
  const [meta, setMeta] = useState({
    customer_id: searchParams.get("customer_id") || "",
    customer_name: "",
    customer_address: "",
    contact: "",
    subject: "",
    sales_person_id: "",
    sales_person_name: "",
    dealer_id: "",
    dealer_name: "",
    project_id: searchParams.get("project_id") || "",
    transportation_cost: 0,
    installation_cost: 0,
    margin_percent: 30,
    discount_percent: 0,
    tax_percent: 18,
    remarks: "",
  });
  const [salesReps, setSalesReps] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [extraItems, setExtraItems] = useState([]);
  const [extraDraft, setExtraDraft] = useState({ description: "", amount: "" });
  const [enquiryId, setEnquiryId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [editIdx, setEditIdx] = useState(null); // null = adding, otherwise index of line being edited

  function emptyDraft() {
    return {
      product: "", cabinet_type: "", module_id: "",
      material_id: "", door_type_id: "", handle_id: "",
      width: "", depth: "", height: "", quantity: 1, accessories: [],
    };
  }

  useEffect(() => {
    (async () => {
      try {
        const [c, cs] = await Promise.all([api.get("/quote/catalog"), api.get("/customers")]);
        setCatalog(c.data);
        setCustomers(cs.data);
        // Load Sales reps for the "Sales person" dropdown on the quote form.
        try {
          const { data: reps } = await api.get("/enquiry/sales");
          setSalesReps(reps || []);
        } catch (_) { /* noop */ }
        try {
          const { data: ds } = await api.get("/enquiry/dealers");
          setDealers(ds || []);
        } catch (_) { /* noop */ }
        const preId = searchParams.get("customer_id");
        if (preId) {
          const cust = cs.data.find((x) => x.id === preId);
          if (cust) setMeta((m) => ({ ...m, customer_id: cust.id, customer_name: cust.name, customer_address: cust.location || "", contact: cust.phone || "" }));
        }
        // Prefill from an enquiry — carries customer name/phone/address/subject and back-links on save.
        const enqId = searchParams.get("enquiry_id");
        if (enqId) {
          try {
            const { data: enq } = await api.get(`/enquiry/${enqId}`);
            setEnquiryId(enq.id);
            setMeta((m) => ({
              ...m,
              customer_name: enq.name || m.customer_name,
              contact: enq.phone || m.contact,
              customer_address: enq.address || m.customer_address,
              subject: (enq.requirements || []).join(", ") || m.subject,
              sales_person_id: enq.assigned_sales_id || m.sales_person_id,
              sales_person_name: enq.assigned_sales_name || m.sales_person_name,
              dealer_id: enq.assigned_dealer_id || m.dealer_id,
              dealer_name: enq.assigned_dealer_name || m.dealer_name,
            }));
          } catch (_) { /* noop */ }
        }
        if (editing) {
          const { data: q } = await api.get(`/quote/quotes/${id}`);
          setMeta({
            customer_id: q.customer_id || "",
            customer_name: q.customer_name || "",
            customer_address: q.customer_address || "",
            contact: q.contact || "",
            subject: q.subject || "",
            sales_person_id: q.sales_person_id || "",
            sales_person_name: q.sales_person_name || "",
            dealer_id: q.dealer_id || "",
            dealer_name: q.dealer_name || "",
            project_id: q.project_id || "",
            transportation_cost: q.transportation_cost || 0,
            installation_cost: q.installation_cost || 0,
            margin_percent: q.margin_percent || 0,
            discount_percent: q.discount_percent || 0,
            tax_percent: q.tax_percent || 18,
            remarks: q.remarks || "",
          });
          setExtraItems(
            Array.isArray(q.extra_items)
              ? q.extra_items.map((e) => ({ description: e.description || "", amount: Number(e.amount || 0) }))
              : []
          );
          setLines((q.lines || []).map((l) => ({
            module_id: l.module_id, material_id: l.material_id,
            door_type_id: l.door_type_id, handle_id: l.handle_id,
            width: l.width, depth: l.depth, height: l.height, quantity: l.quantity,
            accessories: (l.accessories || []).map((a) => ({ accessory_id: a.accessory_id, qty: a.qty })),
          })));
        }
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [id, editing, searchParams]);

  // Live preview when lines change
  useEffect(() => {
    if (!catalog) return;
    if (lines.length === 0) { setPreview(null); return; }
    const t = setTimeout(async () => {
      try {
        const payload = { ...meta, lines, extra_items: extraItems };
        const { data } = await api.post("/quote/preview", payload);
        setPreview(data);
      } catch (e) { /* ignore preview errors */ }
    }, 250);
    return () => clearTimeout(t);
  }, [lines, meta, extraItems, catalog]);

  const cabinetTypesForProduct = useMemo(() => {
    if (!catalog || !draft.product) return [];
    return Object.keys(catalog.tree[draft.product] || {});
  }, [catalog, draft.product]);

  const modulesForCabinet = useMemo(() => {
    if (!catalog || !draft.product || !draft.cabinet_type) return [];
    return catalog.tree[draft.product]?.[draft.cabinet_type] || [];
  }, [catalog, draft.product, draft.cabinet_type]);

  const accessoriesForModule = useMemo(() => {
    if (!catalog || !draft.module_id) return [];
    return (catalog.accessories || []).filter((a) =>
      Array.isArray(a.allowed_module_ids) && a.allowed_module_ids.includes(draft.module_id)
    );
  }, [catalog, draft.module_id]);

  // Selected module doc (has width_options / depth_options / height_options when configured)
  const selectedModule = useMemo(() => {
    if (!catalog || !draft.module_id) return null;
    return (catalog.modules || []).find((m) => m.id === draft.module_id) || null;
  }, [catalog, draft.module_id]);

  const saveLine = () => {
    if (!draft.module_id || !draft.material_id || !draft.width || !draft.depth || !draft.height) {
      toast.error("Module, material and dimensions are required");
      return;
    }
    const newLine = {
      module_id: draft.module_id,
      material_id: draft.material_id,
      door_type_id: draft.door_type_id || null,
      handle_id: draft.handle_id || null,
      width: Number(draft.width), depth: Number(draft.depth), height: Number(draft.height),
      quantity: Number(draft.quantity) || 1,
      accessories: draft.accessories,
    };
    if (editIdx !== null) {
      // Update existing line, preserve accessories order
      const next = [...lines];
      next[editIdx] = { ...newLine, accessories: lines[editIdx].accessories || [] };
      setLines(next);
      toast.success(`Line ${editIdx + 1} updated`);
      setEditIdx(null);
      setDraft(emptyDraft());
    } else {
      setLines([...lines, newLine]);
      // Keep the current selections so the user can quickly add a similar line.
      // Only clear per-line accessories (they get picked separately on each row).
      setDraft({ ...draft, accessories: [] });
    }
    // Scroll to the top of add section on mobile so user sees the change
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const startEditLine = (i) => {
    const l = lines[i];
    const mod = (catalog.modules || []).find((x) => x.id === l.module_id);
    setEditIdx(i);
    setDraft({
      product: mod?.product || "",
      cabinet_type: mod?.cabinet_type || "",
      module_id: l.module_id || "",
      material_id: l.material_id || "",
      door_type_id: l.door_type_id || "",
      handle_id: l.handle_id || "",
      width: l.width || "",
      depth: l.depth || "",
      height: l.height || "",
      quantity: l.quantity || 1,
      accessories: l.accessories || [],
    });
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditIdx(null);
    setDraft(emptyDraft());
  };

  const removeLine = (i) => {
    setLines(lines.filter((_, idx) => idx !== i));
    if (editIdx === i) { setEditIdx(null); setDraft(emptyDraft()); }
    else if (editIdx !== null && editIdx > i) { setEditIdx(editIdx - 1); }
  };
  const updateLineField = (i, field, value) => {
    const next = [...lines];
    next[i] = { ...next[i], [field]: field === "quantity" || field === "width" || field === "depth" || field === "height" ? Number(value) : value };
    setLines(next);
  };

  const addAccessoryToLine = (i, accId) => {
    const next = [...lines];
    next[i].accessories = [...(next[i].accessories || []), { accessory_id: accId, qty: 1 }];
    setLines(next);
  };

  const updateLineAccessoryQty = (i, ai, qty) => {
    const next = [...lines];
    next[i].accessories[ai] = { ...next[i].accessories[ai], qty: Number(qty) };
    setLines(next);
  };

  const removeLineAccessory = (i, ai) => {
    const next = [...lines];
    next[i].accessories = next[i].accessories.filter((_, idx) => idx !== ai);
    setLines(next);
  };

  const save = async () => {
    if (lines.length === 0) { toast.error("Add at least one module"); return; }
    if (!meta.customer_name) { toast.error("Customer name required"); return; }
    setBusy(true);
    try {
      const payload = { ...meta, lines, extra_items: extraItems, enquiry_id: enquiryId };
      const { data } = editing
        ? await api.put(`/quote/quotes/${id}`, payload)
        : await api.post("/quote/quotes", payload);
      toast.success(editing ? "Quote updated" : `Quote ${data.quote_number} created`);
      navigate(`/quotes/${data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!catalog) return <DesktopLayout><div className="text-slate-500">Loading catalog…</div></DesktopLayout>;

  const pickCustomer = (cid) => {
    if (!cid) { setMeta({ ...meta, customer_id: "" }); return; }
    const c = customers.find((x) => x.id === cid);
    if (!c) return;
    setMeta({ ...meta, customer_id: c.id, customer_name: c.name, customer_address: c.location || "", contact: c.phone || "" });
  };

  return (
    <DesktopLayout>
      <div className="mb-6">
        <div className="hk-tiny-label">Quoting</div>
        <h1 className="text-4xl font-black tracking-tighter" style={{ fontFamily: "Chivo" }}>
          {editing ? "Edit quote" : "New quote"}
        </h1>
      </div>

      {/* Customer block */}
      <section className="hk-card p-6 mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className={LABEL}>Existing customer</label>
          <select className={FIELD} value={meta.customer_id} onChange={(e) => pickCustomer(e.target.value)} data-testid="qb-customer-picker">
            <option value="">— New customer —</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}{c.phone ? ` · ${c.phone}` : ""}</option>)}
          </select>
        </div>
        <div>
          <label className={LABEL}>Customer name *</label>
          <input className={FIELD} required value={meta.customer_name} onChange={(e) => setMeta({ ...meta, customer_name: e.target.value })} data-testid="qb-customer-name" />
        </div>
        <div>
          <label className={LABEL}>Contact</label>
          <input className={FIELD} value={meta.contact} onChange={(e) => setMeta({ ...meta, contact: e.target.value })} />
        </div>
        <div className="md:col-span-2">
          <label className={LABEL}>Address</label>
          <input className={FIELD} value={meta.customer_address} onChange={(e) => setMeta({ ...meta, customer_address: e.target.value })} />
        </div>
        <div>
          <label className={LABEL}>Project Name</label>
          <input className={FIELD} value={meta.subject} onChange={(e) => setMeta({ ...meta, subject: e.target.value })} placeholder="e.g. Whitefield Penthouse Kitchen" data-testid="qb-project-name" />
        </div>
        <div>
          <label className={LABEL}>Sales Person</label>
          <select className={FIELD} value={meta.sales_person_id} data-testid="qb-sales-person"
            onChange={(e) => {
              const r = salesReps.find((x) => x.id === e.target.value);
              setMeta({ ...meta, sales_person_id: e.target.value, sales_person_name: r ? r.name : "" });
            }}>
            <option value="">— Select Sales Person —</option>
            {salesReps.map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={LABEL}>Dealer (optional)</label>
          <select className={FIELD} value={meta.dealer_id} data-testid="qb-dealer"
            onChange={(e) => {
              const d = dealers.find((x) => x.id === e.target.value);
              setMeta({ ...meta, dealer_id: e.target.value, dealer_name: d ? d.name : "" });
            }}>
            <option value="">— No dealer —</option>
            {dealers.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        </div>
      </section>

      {/* Module add / edit row */}
      <section
        className={`hk-card p-6 mb-6 ${editIdx !== null ? "border-l-4 border-l-[#E52B50] ring-2 ring-[#E52B50]/10" : ""}`}
        data-testid="qb-add-line"
      >
        <div className="flex items-center justify-between gap-3 mb-3">
          <h3 className="font-bold" style={{ fontFamily: "Chivo" }}>
            {editIdx !== null ? `Edit line #${editIdx + 1}` : "Add a module"}
          </h3>
          {editIdx !== null && (
            <button
              type="button"
              onClick={cancelEdit}
              className="text-sm font-semibold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
              data-testid="qb-cancel-edit"
            >
              <X className="w-4 h-4" /> Cancel edit
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <Picker label="Category" testid="qb-product" value={draft.product} onChange={(v) => setDraft({ ...draft, product: v, cabinet_type: "", module_id: "" })} options={Object.keys(catalog.tree)} />
          <Picker label="Cabinet Type" testid="qb-cabinet" value={draft.cabinet_type} onChange={(v) => setDraft({ ...draft, cabinet_type: v, module_id: "" })} options={cabinetTypesForProduct} disabled={!draft.product} />
          <Picker label="Module" testid="qb-module" value={draft.module_id} onChange={(v) => setDraft({ ...draft, module_id: v })} options={modulesForCabinet.map((m) => [m.id, m.name])} disabled={!draft.cabinet_type} />
          <Picker label="Material" testid="qb-material" value={draft.material_id} onChange={(v) => setDraft({ ...draft, material_id: v })} options={catalog.materials.map((m) => [m.id, m.name])} />
          <Picker label="Door Type" testid="qb-door" value={draft.door_type_id} onChange={(v) => setDraft({ ...draft, door_type_id: v })} options={[["", "— None —"], ...catalog.door_types.map((m) => [m.id, m.name])]} skipEmpty />
          <Picker label="Handle" testid="qb-handle" value={draft.handle_id} onChange={(v) => setDraft({ ...draft, handle_id: v })} options={[["", "— None —"], ...catalog.handles.map((m) => [m.id, m.name])]} skipEmpty />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
          <DimensionInput
            label="Width (mm)"
            value={draft.width}
            options={selectedModule?.width_options}
            onChange={(v) => setDraft({ ...draft, width: v })}
            testid="qb-width"
          />
          <DimensionInput
            label="Depth (mm)"
            value={draft.depth}
            options={selectedModule?.depth_options}
            onChange={(v) => setDraft({ ...draft, depth: v })}
            testid="qb-depth"
          />
          <DimensionInput
            label="Height (mm)"
            value={draft.height}
            options={selectedModule?.height_options}
            onChange={(v) => setDraft({ ...draft, height: v })}
            testid="qb-height"
          />
          <NumberInput label="Quantity" value={draft.quantity} onChange={(v) => setDraft({ ...draft, quantity: v })} testid="qb-qty" />
        </div>
        <button
          type="button"
          onClick={saveLine}
          className="mt-4 inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-5 py-2 rounded-sm"
          data-testid={editIdx !== null ? "qb-update-line" : "qb-add"}
        >
          {editIdx !== null ? <><Pencil className="w-4 h-4" /> Update line</> : <><Plus className="w-4 h-4" /> Add module to quote</>}
        </button>
      </section>

      {/* Lines table */}
      {lines.length > 0 && (
        <section className="hk-card overflow-x-auto mb-6">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 hk-tiny-label">
                <th className="text-left px-3 py-3">#</th>
                <th className="text-left px-3 py-3">Module</th>
                <th className="text-left px-3 py-3">Material</th>
                <th className="text-left px-3 py-3">Door · Handle</th>
                <th className="text-left px-3 py-3">W × D × H</th>
                <th className="text-left px-3 py-3">Qty</th>
                <th className="text-right px-3 py-3">Rate</th>
                <th className="text-right px-3 py-3">Amount</th>
                <th className="text-right px-3 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => {
                const m = (catalog.modules || []).find((x) => x.id === l.module_id) || {};
                const mat = (catalog.materials || []).find((x) => x.id === l.material_id) || {};
                const door = (catalog.door_types || []).find((x) => x.id === l.door_type_id);
                const handle = (catalog.handles || []).find((x) => x.id === l.handle_id);
                const pl = preview?.lines?.[i];
                return (
                  <React.Fragment key={i}>
                    <tr className={`border-t border-slate-100 ${editIdx === i ? "bg-[#E52B50]/5" : ""}`}>
                      <td className="px-3 py-2 font-bold">{i + 1}</td>
                      <td className="px-3 py-2">
                        <div className="font-bold">{m.name}</div>
                        <div className="text-slate-500">{m.cabinet_type}</div>
                      </td>
                      <td className="px-3 py-2">{mat.name}</td>
                      <td className="px-3 py-2">{door?.name || "—"} · {handle?.name || "—"}</td>
                      <td className="px-3 py-2 font-mono">{l.width}×{l.depth}×{l.height}</td>
                      <td className="px-3 py-2">
                        <input type="number" min="1" value={l.quantity} onChange={(e) => updateLineField(i, "quantity", e.target.value)} className="w-16 border border-slate-300 rounded-sm p-1 text-xs" data-testid={`qb-line-qty-${i}`} />
                      </td>
                      <td className="px-3 py-2 text-right font-bold">₹{(pl?.unit_rate || 0).toLocaleString()}</td>
                      <td className="px-3 py-2 text-right font-bold">₹{(pl?.line_total || 0).toLocaleString()}</td>
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex items-center gap-2">
                          <button
                            onClick={() => startEditLine(i)}
                            className="text-slate-500 hover:text-[#E52B50]"
                            title="Edit this line"
                            data-testid={`qb-edit-${i}`}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => removeLine(i)}
                            className="text-red-500 hover:text-red-700"
                            title="Delete this line"
                            data-testid={`qb-remove-${i}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {/* accessories sub-rows */}
                    {(l.accessories || []).map((a, ai) => {
                      const arec = (catalog.accessories || []).find((x) => x.id === a.accessory_id);
                      const pa = pl?.accessories?.[ai];
                      return (
                        <tr key={`a-${ai}`} className="bg-slate-50/60">
                          <td></td>
                          <td colSpan={3} className="px-3 py-1 text-slate-700 italic">
                            <span className="hk-tiny-label mr-2">Acc.</span>
                            {arec?.name} · {arec?.brand} · {arec?.model_number}
                          </td>
                          <td></td>
                          <td className="px-3 py-1">
                            <input type="number" min="1" value={a.qty} onChange={(e) => updateLineAccessoryQty(i, ai, e.target.value)} className="w-14 border border-slate-300 rounded-sm p-1 text-xs" />
                          </td>
                          <td className="px-3 py-1 text-right">₹{(pa?.rate || arec?.rate || 0).toLocaleString()}</td>
                          <td className="px-3 py-1 text-right">₹{(pa?.total || 0).toLocaleString()}</td>
                          <td className="px-3 py-1 text-right">
                            <button onClick={() => removeLineAccessory(i, ai)} className="text-slate-400 hover:text-red-600"><X className="w-3 h-3" /></button>
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="bg-slate-50/30">
                      <td colSpan={9} className="px-3 py-2">
                        <AccessoryAdder catalog={catalog} moduleId={l.module_id} onAdd={(accId) => addAccessoryToLine(i, accId)} />
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </section>
      )}

      {/* Totals + final inputs */}
      <section className="grid md:grid-cols-2 gap-6 mb-6">
        <div className="hk-card p-6 space-y-3">
          <h3 className="font-bold" style={{ fontFamily: "Chivo" }}>Charges & taxes</h3>
          <div className="grid grid-cols-2 gap-3">
            <NumberInput label="Transportation (₹)" value={meta.transportation_cost} onChange={(v) => setMeta({ ...meta, transportation_cost: v })} testid="qb-transport" />
            <NumberInput label="Installation (₹)" value={meta.installation_cost} onChange={(v) => setMeta({ ...meta, installation_cost: v })} testid="qb-install" />
            <NumberInput label="Margin (%)" value={meta.margin_percent} onChange={(v) => setMeta({ ...meta, margin_percent: v })} testid="qb-margin" />
            <NumberInput label="Tax (%)" value={meta.tax_percent} onChange={(v) => setMeta({ ...meta, tax_percent: v })} testid="qb-tax" />
            <div className="col-span-2">
              <NumberInput label="Discount on Modules (%)"
                value={meta.discount_percent}
                onChange={(v) => setMeta({ ...meta, discount_percent: Math.max(0, Math.min(100, Number(v) || 0)) })}
                testid="qb-discount" />
              <p className="text-[10px] text-slate-500 mt-1">
                Applied only to the <b>Modules</b> total (after margin). Leave 0 to hide the discount row in the quote.
              </p>
            </div>
          </div>
          <div>
            <label className={LABEL}>Remarks</label>
            <textarea className={FIELD} rows={3} value={meta.remarks} onChange={(e) => setMeta({ ...meta, remarks: e.target.value })} />
          </div>

          {/* Extra items (after-tax add-ons like appliances, counter top, etc.) */}
          <div className="pt-3 border-t border-slate-200">
            <label className={LABEL + " mb-2"}>Additional items (added after tax)</label>
            {extraItems.length > 0 && (
              <ul className="space-y-1.5 mb-3" data-testid="qb-extras-list">
                {extraItems.map((ex, i) => (
                  <li
                    key={i}
                    className="flex items-center justify-between gap-2 bg-slate-50 border border-slate-200 rounded-sm px-3 py-2 text-sm"
                    data-testid={`qb-extra-row-${i}`}
                  >
                    <span className="font-semibold text-slate-800 truncate">{ex.description || "Additional charge"}</span>
                    <span className="font-mono font-bold text-slate-900 whitespace-nowrap">
                      ₹{Number(ex.amount || 0).toLocaleString()}
                    </span>
                    <button
                      type="button"
                      onClick={() => setExtraItems(extraItems.filter((_, idx) => idx !== i))}
                      className="text-red-500 hover:text-red-700"
                      title="Remove"
                      data-testid={`qb-extra-remove-${i}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="grid grid-cols-[1fr_120px_auto] gap-2">
              <input
                type="text"
                className={FIELD}
                placeholder="Description (e.g. Appliances, Counter Top)"
                value={extraDraft.description}
                onChange={(e) => setExtraDraft({ ...extraDraft, description: e.target.value })}
                data-testid="qb-extra-desc"
              />
              <input
                type="number"
                min="0"
                className={FIELD}
                placeholder="Amount (₹)"
                value={extraDraft.amount}
                onChange={(e) => setExtraDraft({ ...extraDraft, amount: e.target.value })}
                data-testid="qb-extra-amount"
              />
              <button
                type="button"
                onClick={() => {
                  const desc = (extraDraft.description || "").trim();
                  const amt = Number(extraDraft.amount);
                  if (!desc) return toast.error("Description is required");
                  if (!amt || amt <= 0) return toast.error("Amount must be greater than 0");
                  setExtraItems([...extraItems, { description: desc, amount: amt }]);
                  setExtraDraft({ description: "", amount: "" });
                }}
                className="bg-slate-800 hover:bg-slate-900 text-white font-bold px-4 py-2 rounded-sm text-sm inline-flex items-center gap-1"
                data-testid="qb-extra-add"
              >
                <Plus className="w-3.5 h-3.5" /> Add to quote
              </button>
            </div>
            <p className="text-[11px] text-slate-500 mt-1.5">
              These items are added to the grand total <b>after</b> tax and are not taxed.
              Leave empty to skip.
            </p>
          </div>
        </div>
        <div className="hk-card p-6">
          <h3 className="font-bold mb-3" style={{ fontFamily: "Chivo" }}>Summary</h3>
          {preview?.totals ? (
            <table className="w-full text-sm">
              <tbody>
                <Row label="Modules" value={preview.totals.modules_total} />
                {Number(preview.totals.discount_amount) > 0 && (
                  <Row label={`Less: Discount on Modules (${preview.totals.discount_percent}%)`}
                    value={-preview.totals.discount_amount}
                    testid="qb-summary-discount" />
                )}
                <Row label="Accessories" value={preview.totals.accessories_total} />
                <Row label="Transportation" value={preview.totals.transportation_cost} />
                <Row label="Installation" value={preview.totals.installation_cost} />
                <Row label="Subtotal" value={preview.totals.subtotal} bold />
                <Row label={`Tax (${preview.totals.tax_percent}%)`} value={preview.totals.tax} />
                {(preview.totals.extra_items || []).map((ex, i) => (
                  <Row
                    key={`ex-${i}`}
                    label={ex.description || "Additional charge"}
                    value={ex.amount}
                    testid={`qb-summary-extra-${i}`}
                  />
                ))}
                <tr className="border-t-2 border-slate-900"><td className="py-2 font-black">Grand total</td><td className="text-right py-2 font-black text-lg" data-testid="qb-grand-total">₹{(preview.totals.grand_total || 0).toLocaleString()}</td></tr>
              </tbody>
            </table>
          ) : <p className="text-sm text-slate-500">Add a module to see the cost.</p>}
        </div>
      </section>

      <div className="flex items-center gap-3 mb-10">
        <button onClick={save} disabled={busy} className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-2.5 rounded-sm disabled:opacity-60" data-testid="qb-save">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} {editing ? "Save changes" : "Save quote"}
        </button>
        <button onClick={() => navigate("/quotes")} className="text-slate-600 hover:text-slate-900 text-sm font-semibold">Cancel</button>
      </div>
    </DesktopLayout>
  );
}

function Picker({ label, value, onChange, options, disabled, testid, skipEmpty }) {
  return (
    <div>
      <label className={LABEL}>{label}</label>
      <select className={FIELD} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} data-testid={testid}>
        {!skipEmpty && <option value="">— Select —</option>}
        {options.map((o) => {
          if (Array.isArray(o)) return <option key={o[0]} value={o[0]}>{o[1]}</option>;
          return <option key={o} value={o}>{o}</option>;
        })}
      </select>
    </div>
  );
}

function NumberInput({ label, value, onChange, testid }) {
  return (
    <div>
      <label className={LABEL}>{label}</label>
      <input type="number" className={FIELD} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}

/**
 * Dimension input that becomes a dropdown when `options` is a non-empty array,
 * otherwise falls back to free-typing (backward-compatible with modules that
 * haven't been configured yet).
 */
function DimensionInput({ label, value, onChange, options, testid }) {
  const opts = Array.isArray(options) ? options.filter((x) => x != null && x !== "") : [];
  if (opts.length === 0) {
    return <NumberInput label={label} value={value} onChange={onChange} testid={testid} />;
  }
  // If the currently-selected value is not in the presets, keep it visible so users don't lose data on module change
  const cur = value === "" || value == null ? "" : Number(value);
  const includesCur = opts.some((o) => Number(o) === cur);
  return (
    <div>
      <label className={LABEL}>{label}</label>
      <select
        className={FIELD}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      >
        <option value="">— Select —</option>
        {opts.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
        {cur !== "" && !includesCur && (
          <option value={cur}>{cur} (custom)</option>
        )}
      </select>
    </div>
  );
}

function Row({ label, value, bold, testid }) {
  const num = Number(value || 0);
  const isNeg = num < 0;
  const display = `${isNeg ? "-" : ""}₹${Math.abs(num).toLocaleString()}`;
  return (
    <tr className={bold ? "font-bold border-t border-slate-300" : ""} data-testid={testid}>
      <td className="py-1">{label}</td>
      <td className={`text-right py-1 ${isNeg ? "text-emerald-700" : ""}`}>{display}</td>
    </tr>
  );
}

function AccessoryAdder({ catalog, moduleId, onAdd }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState("");
  const list = (catalog.accessories || []).filter((a) =>
    Array.isArray(a.allowed_module_ids) && a.allowed_module_ids.includes(moduleId)
  );
  if (!open) {
    return <button onClick={() => setOpen(true)} className="text-orange-600 hover:text-orange-700 text-xs font-bold inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add accessory</button>;
  }
  if (list.length === 0) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-xs italic text-slate-500">
          No accessories are assigned to this module. Assign some in Quote Settings → Accessories.
        </span>
        <button onClick={() => setOpen(false)} className="text-slate-500"><X className="w-4 h-4" /></button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <select className={FIELD + " max-w-xs"} value={selected} onChange={(e) => setSelected(e.target.value)}>
        <option value="">— Pick accessory —</option>
        {list.map((a) => <option key={a.id} value={a.id}>{a.name} · {a.brand} · ₹{a.rate}</option>)}
      </select>
      <button onClick={() => { if (selected) { onAdd(selected); setSelected(""); setOpen(false); } }} className="bg-slate-800 text-white px-3 py-1 rounded-sm text-xs font-bold">Add</button>
      <button onClick={() => setOpen(false)} className="text-slate-500"><X className="w-4 h-4" /></button>
    </div>
  );
}
